import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material'
import { ButtonForm, CustomTextField } from 'Components/Form/styles'
import { Column, Row } from 'Components/Grid'
import { MuiChipsInput } from 'mui-chips-input'
import { Controller, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useCountryConfig } from 'hooks/useCountryConfig'
import { validateTaxId, stripTaxIdFormatting } from 'utils/tax_id_validators'

export const Detalhes = ({
    handleNext,
    register,
    control,
    handleSubmit,
    errors,
    getValues
}) => {
    const navigate = useNavigate()
    const countryValue = useWatch({ control, name: 'country' })
    const { taxIdLabel, taxIdMask, taxIdPlaceholder, countryConfig, allConfigs, isError: isCountryConfigError, isLoading: isCountryConfigLoading } = useCountryConfig(countryValue)
    const onSubmit = () => {
        handleNext()
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('name', { required: 'Nome é obrigatório' })}
                        label="Nome"
                        autoFocus
                        error={errors?.name ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.name ? errors.name.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('corporateName', { required: 'Razão social é obrigatório' })}
                        label="Razão social"
                        autoFocus
                        error={errors?.corporateName ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.corporateName ? errors.corporateName.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('slug', { required: 'Identificador é obrigatório' })}
                        label="Identificador"
                        error={errors?.slug ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.slug ? errors.slug.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="country"
                        rules={{ required: 'País é obrigatório' }}
                        render={({ field: { onChange, value } }) => (
                            <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!errors?.country || isCountryConfigError}>
                                <InputLabel id="country-label" shrink={true}>País</InputLabel>
                                <Select
                                    labelId="country-label"
                                    value={value}
                                    onChange={onChange}
                                    notched
                                    label="País"
                                    displayEmpty
                                    disabled={isCountryConfigError}
                                >
                                    <MenuItem value="">
                                        {isCountryConfigError ? 'Erro ao carregar países' : 'Selecione um País'}
                                    </MenuItem>
                                    {allConfigs.map(cc => (
                                        <MenuItem key={cc.countryCode} value={cc.countryCode}>
                                            {cc.name.pt}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {isCountryConfigError && <FormHelperText>Não foi possível carregar os países. Recarregue a página.</FormHelperText>}
                                <FormHelperText>
                                    {errors?.country ? errors.country.message : ''}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="defaultLanguage"
                        render={({ field: { onChange, value } }) => (
                            <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!errors?.defaultLanguage}>
                                <InputLabel id="language-label" shrink={true}>Idioma</InputLabel>
                                <Select
                                    labelId="language-label"
                                    value={value}
                                    onChange={onChange}
                                    notched
                                    label="Idioma"
                                    displayEmpty
                                >
                                    <MenuItem value="">Selecione um idioma</MenuItem>
                                    <MenuItem value="pt">Português</MenuItem>
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="es">Español</MenuItem>
                                </Select>
                                <FormHelperText>
                                    {errors?.defaultLanguage ? errors.defaultLanguage.message : ''}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('cnpj', {
                            required: `${taxIdLabel || 'Tax ID'} é obrigatório`,
                            validate: (value) => {
                                if (!value || !countryValue) return true;
                                if (isCountryConfigLoading) return 'Aguardando configuração de país...';
                                if (!countryConfig) return true;
                                const stripped = stripTaxIdFormatting(value);
                                // Only run format regex when value has visual separators (formatted input)
                                // Skip for already-stripped DB values on edit to avoid false failures
                                if (stripped !== value && countryConfig.taxId?.regex) {
                                    const formatOk = new RegExp(countryConfig.taxId.regex).test(value);
                                    if (!formatOk) return `Formato inválido. Use: ${taxIdMask}`;
                                }
                                const checkOk = validateTaxId(stripped, countryValue);
                                if (!checkOk) return `${taxIdLabel} inválido. Use o formato: ${taxIdMask}`;
                                return true;
                            },
                        })}
                        label={taxIdLabel || 'CNPJ'}
                        placeholder={taxIdPlaceholder}
                        error={errors?.cnpj ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.cnpj ? errors.cnpj.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('size')}
                        label="Quantidade de funcionários"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('income')}
                        label="Receita"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <CustomTextField
                        {...register('industry')}
                        label="Indústria"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <FormControl
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                    >
                        <Controller
                            control={control}
                            name="domains"
                            render={({ field: { onChange, value }}) => (
                                <MuiChipsInput
                                label="Domínios" 
                                value={value} 
                                onChange={onChange}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Digite e aperte enter"
                                renderChip={(Component, props) => {
                                    return (
                                    <Component 
                                        {...props}
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                        width: 'fit-content',
                                        }}
                                    />
                                    )
                                }}
                                sx={{
                                    '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                                    width: 'fit-content'
                                    },
                                    '& .MuiButtonBase-root.MuiIconButton-root': {
                                    width: 'fit-content',
                                    marginTop: '0px'
                                    }
                                }}/>
                            )}
                        ></Controller>
                    </FormControl>
                </Column>
            </Row>
            <Row>
                <Column xs={12} sm={6} md={6} className="d-flex">
                    <Controller
                        control={control}
                        name="active"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="active" />}
                                label="Ativo"
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                </Column>
                <Column xs={12} sm={6} md={6} className="d-flex">
                    <Controller
                        control={control}
                        name="partner"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="partner" />}
                                label="Parceiro"
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        rules={{
                        validate: () => {
                            return getValues('signedPlan') !== ''
                        },
                        required: 'Plano de assinatua é obrigatório',
                        }}
                        name="signedPlan"
                        render={({ field: { onChange, value } }) => (
                        <FormControl
                            fullWidth
                            sx={{ marginBottom: '24px' }}
                            error={errors?.signedPlan ? true : false}
                        >
                            <InputLabel id="signed-plan-label" shrink={true}>Plano de assinatura</InputLabel>
                            <Select
                                labelId="signed-plan-label"
                                id="signedPlan"
                                value={value}
                                notched
                                label="Plano de assinatura"
                                onChange={onChange}
                            >
                                <MenuItem value="" key={-1}>
                                    Selecione um Plano de assinatura
                                </MenuItem>
                                {['Free', 'Starter', 'Premium'].map((item, index) => (
                                    <MenuItem value={item} key={index}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {errors?.signedPlan ? errors.signedPlan.message : ''}
                            </FormHelperText>
                        </FormControl>
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column>
                    <TextField
                    {...register('description')}
                    label="Descrição"
                    multiline
                    rows={4}
                    sx={{ margin: '0 auto 24px', width: '100%' }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="end" alignItems="center">
                <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
                <ButtonForm
                    secondary
                    id="cancel"
                    label="cancelar"
                    onClick={() => navigate('/companies')}
                ></ButtonForm>
                <ButtonForm label="Avançar" type="submit" />
                </Column>
            </Row>  
        </form>
    )
}